// src/features/services/sales/components/BomPanel.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Bill of Materials management panel.
// PAC Glass surfaces. Data owned by useBom + useSkuCatalog hooks.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { useBom } from '../hooks/useBom';
import { useSkuCatalog } from '../hooks/useSkuCatalog';
import type { BomDefinition, UpsertBomPayload } from '../api/bomApi';

interface ComponentRow {
  sku: string;
  quantity: number;
  unit: string;
  label: string;
}

export default function BomPanel() {
  const { data, isLoading, isError, error, upsert } = useBom({ active: true });
  const skuQuery = useSkuCatalog();
  const allSkus = skuQuery.data?.items ?? [];

  // Finished SKUs = assembly type, Component SKUs = component type
  const assemblySkus = allSkus.filter(s => (s.sku_type ?? 'assembly') === 'assembly' && s.active);
  const componentSkus = allSkus.filter(s => s.sku_type === 'component' && s.active);

  const [showForm, setShowForm] = useState(false);
  const [finishedSku, setFinishedSku] = useState('');
  const [label, setLabel] = useState('');
  const [components, setComponents] = useState<ComponentRow[]>([
    { sku: '', quantity: 1, unit: 'pcs', label: '' },
  ]);

  const resetForm = useCallback(() => {
    setFinishedSku('');
    setLabel('');
    setComponents([{ sku: '', quantity: 1, unit: 'pcs', label: '' }]);
    setShowForm(false);
  }, []);

  const addComponent = () =>
    setComponents(prev => [...prev, { sku: '', quantity: 1, unit: 'pcs', label: '' }]);

  const removeComponent = (idx: number) =>
    setComponents(prev => prev.filter((_, i) => i !== idx));

  const updateComponent = (idx: number, field: keyof ComponentRow, value: string | number) =>
    setComponents(prev => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!finishedSku) return;

    const validComponents = components.filter(c => c.sku.trim());
    if (validComponents.length === 0) return;

    const payload: UpsertBomPayload = {
      finished_sku: finishedSku,
      label: label || undefined,
      components: validComponents.map(c => ({
        sku: c.sku,
        quantity: c.quantity,
        unit: c.unit,
        label: c.label || c.sku,
      })),
    };

    upsert.mutate(payload, { onSuccess: () => resetForm() });
  };

  const boms: BomDefinition[] = data?.items ?? [];

  return (
    <div className="pac-glass rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-neutral-200 uppercase tracking-wider">
          Bill of Materials
        </h2>
        <button
          onClick={() => setShowForm(f => !f)}
          className="pac-glass-btn text-xs px-3 py-1.5 rounded border border-white/10 text-neutral-300
                     hover:border-emerald-500/50 hover:text-emerald-400 transition-colors"
        >
          {showForm ? 'Cancel' : '+ New BOM'}
        </button>
      </div>

      {/* ── Create BOM Form ─────────────────────────────────────────── */}
      {showForm && (
        <form onSubmit={handleSubmit} className="pac-glass-raised rounded p-4 space-y-4 border border-neutral-800">
          {/* Finished SKU */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Finished Product SKU
            </label>
            <select
              value={finishedSku}
              onChange={e => setFinishedSku(e.target.value)}
              className="pac-glass-input w-full rounded px-3 py-2 text-sm text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                         focus:ring-1 focus:ring-emerald-500/30 outline-none"
              required
            >
              <option value="">Select assembly SKU…</option>
              {assemblySkus.map(s => (
                <option key={s.sku} value={s.sku}>{s.label} ({s.sku})</option>
              ))}
            </select>
          </div>

          {/* Label */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              BOM Label
            </label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. PACDaemon IoT Assembly BOM"
              className="pac-glass-input w-full rounded px-3 py-2 text-sm text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                         focus:ring-1 focus:ring-emerald-500/30 outline-none"
            />
          </div>

          {/* Components */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[10px] text-neutral-500 uppercase tracking-wider">
                Components
              </label>
              <button
                type="button"
                onClick={addComponent}
                className="text-[10px] text-emerald-400 hover:text-emerald-300"
              >
                + Add Component
              </button>
            </div>

            {components.map((c, i) => (
              <div key={i} className="flex gap-2 items-end">
                <div className="flex-1">
                  <select
                    value={c.sku}
                    onChange={e => updateComponent(i, 'sku', e.target.value)}
                    className="pac-glass-input w-full rounded px-2 py-1.5 text-xs text-neutral-200
                               bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                               outline-none"
                    required
                  >
                    <option value="">Select component…</option>
                    {componentSkus.map(s => (
                      <option key={s.sku} value={s.sku}>{s.label} ({s.sku})</option>
                    ))}
                    {/* Also allow assembly SKUs as components (sub-assemblies) */}
                    {assemblySkus
                      .filter(s => s.sku !== finishedSku)
                      .map(s => (
                        <option key={s.sku} value={s.sku}>{s.label} ({s.sku})</option>
                      ))}
                  </select>
                </div>
                <input
                  type="number"
                  value={c.quantity}
                  onChange={e => updateComponent(i, 'quantity', Number(e.target.value))}
                  min={1}
                  className="pac-glass-input w-20 rounded px-2 py-1.5 text-xs text-neutral-200
                             bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                             outline-none text-center"
                />
                <span className="text-[10px] text-neutral-500 w-8">pcs</span>
                {components.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeComponent(i)}
                    className="text-xs text-rose-400 hover:text-rose-300 px-1"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={upsert.isPending}
              className="pac-glass-btn px-4 py-2 rounded text-sm border border-emerald-500/40
                         text-emerald-400 hover:border-emerald-400 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {upsert.isPending ? 'Saving…' : 'Save BOM'}
            </button>
          </div>

          {upsert.isError && (
            <p className="text-xs text-rose-400">{upsert.error.message}</p>
          )}
          {upsert.isSuccess && (
            <p className="text-xs text-emerald-400">BOM saved.</p>
          )}
        </form>
      )}

      {/* ── BOM List ────────────────────────────────────────────────── */}
      {isLoading && <p className="text-sm text-neutral-500 animate-pulse">Loading BOMs…</p>}
      {isError && <p className="text-sm text-rose-400">{(error as Error)?.message}</p>}

      {!isLoading && boms.length === 0 && (
        <p className="text-sm text-neutral-500">No BOMs defined yet.</p>
      )}

      {boms.length > 0 && (
        <div className="space-y-2">
          {boms.map(bom => (
            <BomCard key={bom.bom_key} bom={bom} />
          ))}
        </div>
      )}
    </div>
  );
}

function BomCard({ bom }: { bom: BomDefinition }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="pac-glass-subtle rounded p-3 border border-white/5">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left flex items-center gap-3"
      >
        <span className="text-xs font-mono text-neutral-300">{bom.bom_key}</span>
        <span className="text-xs text-neutral-400 flex-1 truncate">{bom.label}</span>
        <span className="text-[10px] text-neutral-500">{bom.components.length} components</span>
        <span className="text-[10px] text-neutral-600">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-white/5 space-y-1">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
            Finished SKU: <span className="text-purple-400">{bom.finished_sku}</span>
          </p>
          {bom.components.map((c, i) => (
            <div key={i} className="flex items-center gap-3 text-xs text-neutral-300 pl-2">
              <span className="text-emerald-400 w-6 text-right">{c.quantity}×</span>
              <span className="font-mono">{c.sku}</span>
              <span className="text-neutral-500">({c.unit})</span>
            </div>
          ))}
          {bom.notes && (
            <p className="text-[10px] text-neutral-500 italic mt-1">{bom.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}
